import 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { UnbalancedRowInfo } from '@/utils/glSubmitValidation';

interface GlSubmitUnbalancedDialogProps {
    open: boolean;
    rows: UnbalancedRowInfo[];
    confirming?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

function formatDiff(diff: number): string {
    const abs = Math.abs(diff).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    if (diff > 0) return `+${abs}`;
    if (diff < 0) return `-${abs}`;
    return abs;
}

export default function GlSubmitUnbalancedDialog(props: GlSubmitUnbalancedDialogProps) {
    const { open, rows, confirming, onCancel, onConfirm } = props;

    return (
        <Dialog
            open={open}
            onClose={confirming ? undefined : onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningAmberIcon color="warning" />
                    <Typography variant="h6" fontWeight={700} component="span">
                        Unbalanced Rows
                    </Typography>
                    <Chip
                        label={`${rows.length} row${rows.length === 1 ? '' : 's'}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 1.5 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The following rows have Dr/Cr amounts that do not net to zero. You may fix them in
                    {' '}
                    <strong>Edit</strong>
                    {' '}
                    mode, or submit anyway if this is expected.
                </Alert>

                <TableContainer sx={{ maxHeight: 320, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 120 }} align="right">
                                    Difference
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.rowIndex} hover>
                                    <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                                        {row.rowIndex + 1}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 13, maxWidth: 280 }}>
                                        <Box
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={row.description}
                                        >
                                            {row.description}
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            fontVariantNumeric: 'tabular-nums',
                                            color: row.diff > 0 ? 'success.main' : 'error.main',
                                        }}
                                    >
                                        {formatDiff(row.diff)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                <Button
                    onClick={onCancel}
                    disabled={confirming}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={onConfirm}
                    disabled={confirming}
                    sx={{ textTransform: 'none', minWidth: 120 }}
                >
                    {confirming ? 'Submitting…' : 'Submit anyway'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
